import { dateRangeFilterValueToRange } from '@app/components/date-range-selector/date-range-filter-value-to-range';
import { parseAsDateRangeFilter } from '@app/components/date-range-selector/parse-as-date-range-filter';
import type { GenericButtonGroupFilterProps } from '@app/core/list-view/base-filters/GenericButtonGroupFilter';
import { GenericButtonGroupFilter } from '@app/core/list-view/base-filters/GenericButtonGroupFilter';
import { GenericDateRangeFilter } from '@app/core/list-view/base-filters/GenericDateRangeFilter';
import { GenericTextFilter } from '@app/core/list-view/base-filters/GenericTextFilter';
import { parseAsCommaSeparatedList } from '@app/core/list-view/helpers/parse-as-comma-separated-list';
import { createFilter } from '@app/core/list-view/types';
import { PlatformSelectFilter } from '@app/modules/social-export/components/inputs/PlatformSelectFilter';
import { parseAsBoolean, parseAsString } from 'nuqs';
import { defineMessage } from 'react-intl';
import { FragmentsOnlyMineFilter } from './FragmentsOnlyMineFilter';
import { UserSelectFilter } from './GenericUserSelectFilter';

export const FRAGMENTS_FILTERS_CONFIG = [
  createFilter({
    name: 'dateRange',
    label: false,
    parser: parseAsDateRangeFilter.withDefault({ preset: 'last7Days' }),
    isPermanent: true,
    component: GenericDateRangeFilter,
    componentProps: {},
    toApi: (val, context) => {
      const { from, to } = dateRangeFilterValueToRange(val, {
        timezone: context?.timezone as string | undefined,
      });

      const filters: string[] = [];

      if (from && to) {
        filters.push(`createdAt||$between||${from},${to}`);
      } else {
        if (from) {
          filters.push(`createdAt||$gte||${from}`);
        }

        if (to) {
          filters.push(`createdAt||$lte||${to}`);
        }
      }

      return filters;
    },
  }),
  createFilter({
    name: 'title',
    label: defineMessage({
      id: 'fragments.props.title',
      defaultMessage: 'Título',
    }),
    parser: parseAsString.withDefault(''),
    isPermanent: true,
    component: GenericTextFilter,
    componentProps: {
      sx: {
        width: '22ch',
      },
    },
    toApi: (val) => [`title||$contL||${val}`],
  }),
  createFilter({
    name: 'onlyMine',
    label: defineMessage({
      id: 'fragments.filter.onlyMine',
      defaultMessage: 'Solo míos',
    }),
    parser: parseAsBoolean.withDefault(false),
    isPermanent: true,
    component: FragmentsOnlyMineFilter,
    toApi: (val, context) => {
      if (!val) {
        return null;
      }

      const userId = context?.userId;
      if (typeof userId !== 'string' || !userId) {
        return null;
      }

      return [`createdById||eq||${userId}`];
    },
  }),
  createFilter({
    name: 'tags',
    label: defineMessage({
      id: 'fragments.props.tag',
      defaultMessage: 'Tag',
    }),
    parser: parseAsString.withDefault(''),
    isPermanent: false,
    component: GenericTextFilter,
    componentProps: {},
    toApi: (val) => [`tags||$intersectsArr||${val}`],
  }),
  createFilter({
    name: 'createdById',
    label: defineMessage({
      id: 'fragments.props.createdBy',
      defaultMessage: 'Creado por',
    }),
    parser: parseAsString,
    isPermanent: false,
    component: UserSelectFilter,
    componentProps: {
      removable: true,
      excludeClientAdmins: false,
    },
    toApi: (val) => [`createdById||eq||${val}`],
  }),
  createFilter<GenericButtonGroupFilterProps, boolean>({
    name: 'hasContentAi',
    label: defineMessage({
      id: 'fragments.filter.hasContentAi',
      defaultMessage: 'Contenido AI',
    }),
    parser: parseAsBoolean,
    isPermanent: false,
    raw: true,
    component: GenericButtonGroupFilter,
    componentProps: {
      options: [
        {
          value: true,
          message: defineMessage({ id: 'common.text.yes', defaultMessage: 'Sí' }),
          color: 'success',
        },
        {
          value: false,
          message: defineMessage({ id: 'common.text.no', defaultMessage: 'No' }),
          color: 'error',
        },
      ],
    },
    toApi: (val) => (val ? 'true' : 'false'),
  }),
  createFilter({
    name: 'hasExternalDelivery',
    label: defineMessage({
      id: 'fragments.filter.hasExternalDelivery',
      defaultMessage: 'Export externo',
    }),
    parser: parseAsBoolean,
    isPermanent: false,
    raw: true,
    component: GenericButtonGroupFilter,
    componentProps: {
      options: [
        {
          value: true,
          message: defineMessage({ id: 'common.text.yes', defaultMessage: 'Sí' }),
          color: 'success',
        },
        {
          value: false,
          message: defineMessage({ id: 'common.text.no', defaultMessage: 'No' }),
          color: 'error',
        },
      ],
    },
    toApi: (val) => (val ? 'true' : 'false'),
  }),
  createFilter({
    name: 'publishedPlatforms',
    label: defineMessage({
      id: 'fragments.filter.publishedPlatforms',
      defaultMessage: 'Plataformas publicadas',
    }),
    parser: parseAsCommaSeparatedList.withDefault([]),
    isPermanent: false,
    raw: true,
    component: PlatformSelectFilter,
    componentProps: {
      sx: {
        minWidth: '20ch',
      },
    },
    toApi: (val) => (val?.length ? val.join(',') : undefined),
  }),
];
